import { useState } from "react";
import { Flame } from "lucide-react";
import { toast } from "sonner";
import { IMAGES, waLink } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import { GoldDivider } from "@/components/GoldDivider";
import { StarField } from "@/components/StarField";
import { useLang } from "@/i18n";

const INITIAL = {
    nombre: "",
    fecha: "",
    signo: "",
    telefono: "",
    email: "",
    paraQuien: "paraMi",
    intencion: "",
    situacion: "",
    entrega: "",
    direccion: "",
    ciudad: "",
    estado: "",
    cp: "",
    confirm: false,
};

const Label = ({ children, htmlFor }) => (
    <label htmlFor={htmlFor} className="block font-accent text-[10px] tracking-[0.22em] uppercase text-gold-soft mb-2">
        {children}
    </label>
);

const PillGroup = ({ options, value, onChange, name }) => (
    <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label={name}>
        {options.map((opt) => (
            <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={value === opt.value}
                data-testid={`${name}-option-${String(opt.value).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => onChange(opt.value)}
                className={`px-4 py-2 rounded-sm border font-body text-xs tracking-wide transition-[border-color,background-color,color,box-shadow] duration-300 ${
                    value === opt.value
                        ? "border-gold bg-gold/15 text-gold shadow-[0_0_14px_rgba(212,175,55,0.18)]"
                        : "border-gold/20 text-cream/60 hover:border-gold/45 hover:text-cream"
                }`}
            >
                {opt.label}
            </button>
        ))}
    </div>
);

export const CandleOrder = () => {
    const { t } = useLang();
    const [form, setForm] = useState(INITIAL);
    const set = (key) => (e) => {
        const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
        setForm((f) => ({ ...f, [key]: v }));
    };

    const intenciones = t("candle.intenciones");
    const signos = t("candle.signos");
    const paraQuienOpts = [
        { value: "paraMi", label: t("candle.opt.paraMi") },
        { value: "otra", label: t("candle.opt.otra") },
    ];
    const entregaOpts = [
        { value: "recoger", label: t("candle.opt.recoger") },
        { value: "envio", label: t("candle.opt.envio") },
    ];

    const handleSubmit = (e) => {
        e.preventDefault();
        const missing = [];
        if (!form.nombre.trim()) missing.push(t("candle.f.nombre").toLowerCase());
        if (!form.fecha) missing.push(t("candle.f.fecha").toLowerCase());
        if (!form.signo) missing.push(t("candle.f.signo").toLowerCase());
        if (!form.telefono.trim()) missing.push(t("candle.f.telefono").toLowerCase());
        if (!form.intencion) missing.push(t("candle.f.intencion").toLowerCase());
        if (!form.situacion.trim()) missing.push(t("candle.f.situacion").toLowerCase());
        if (!form.entrega) missing.push(t("candle.f.entrega").toLowerCase());
        if (form.entrega === "envio" && !form.direccion.trim()) missing.push(t("candle.f.direccion").toLowerCase());
        if (!form.confirm) missing.push(t("candle.f.confirm").toLowerCase());
        if (missing.length) {
            toast.error(t("candle.toastErr") + missing.join(", ") + ".");
            return;
        }
        const w = (k) => t(`candle.wa.${k}`);
        const paraQuienLabel = paraQuienOpts.find((o) => o.value === form.paraQuien)?.label;
        const entregaLabel = entregaOpts.find((o) => o.value === form.entrega)?.label;
        const lines = [
            w("intro"),
            `${w("name")}: ${form.nombre}`,
            `${w("dob")}: ${form.fecha}`,
            `${w("sign")}: ${form.signo}`,
            `${w("forWhom")}: ${paraQuienLabel}`,
            `${w("intention")}: ${form.intencion}`,
            `${w("situation")}: ${form.situacion}`,
            `${w("delivery")}: ${entregaLabel}`,
        ];
        if (form.entrega === "envio") {
            lines.push(`${w("shipAddress")}: ${form.direccion}, ${form.ciudad}, ${form.estado} ${form.cp}`.replace(/,\s*,/g, ",").trim());
        }
        lines.push(`${w("phone")}: ${form.telefono}`);
        if (form.email.trim()) lines.push(`${w("email")}: ${form.email}`);
        window.open(waLink(lines.join("\n")), "_blank", "noopener,noreferrer");
        toast.success(t("candle.toastOk"));
    };

    const inputId = (k) => `candle-${k}`;

    return (
        <section id="velas" data-testid="candle-section" className="relative py-24 sm:py-32 px-5 sm:px-8 bg-ink-deep overflow-hidden">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.22]"
                style={{ backgroundImage: `url(${IMAGES.candles})` }}
                aria-hidden="true"
            />
            <div
                className="absolute inset-0 pointer-events-none candle-glow"
                style={{
                    background:
                        "radial-gradient(ellipse 45% 35% at 12% 18%, rgba(212,175,55,0.12) 0%, transparent 65%), radial-gradient(ellipse 55% 40% at 90% 90%, rgba(74,14,28,0.55) 0%, transparent 65%)",
                }}
                aria-hidden="true"
            />
            <StarField count={40} />
            <div className="absolute top-0 left-0 right-0 h-32 section-fade-top" aria-hidden="true" />

            <div className="relative max-w-6xl mx-auto">
                <Reveal className="text-center mb-14">
                    <div className="flex justify-center mb-5">
                        <span className="w-14 h-14 rounded-full border border-gold/40 flex items-center justify-center shadow-[0_0_24px_rgba(212,175,55,0.25)]">
                            <Flame className="w-6 h-6 text-gold candle-glow" strokeWidth={1.5} />
                        </span>
                    </div>
                    <h2 className="font-display font-semibold gold-text text-4xl sm:text-5xl tracking-wide">
                        {t("candle.title")}
                    </h2>
                    <p className="font-display italic text-lg sm:text-xl text-cream/85 mt-4">
                        {t("candle.subtitle")}
                    </p>
                    <GoldDivider className="my-7" />
                    <p className="text-sm sm:text-base text-cream/65 font-light max-w-2xl mx-auto leading-relaxed">
                        {t("candle.intro")}
                    </p>
                </Reveal>

                <Reveal delay={0.1}>
                    <form
                        onSubmit={handleSubmit}
                        data-testid="candle-order-form"
                        className="ornament-frame bg-surface/85 backdrop-blur-md border border-gold/25 rounded-sm p-6 sm:p-10 lg:p-14 shadow-[0_0_50px_rgba(0,0,0,0.5)]"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div>
                                <Label htmlFor={inputId("nombre")}>{t("candle.f.nombre")}</Label>
                                <input id={inputId("nombre")} data-testid="candle-nombre-input" className="form-input"
                                    value={form.nombre} onChange={set("nombre")} placeholder={t("candle.ph.nombre")} autoComplete="name" />
                            </div>
                            <div>
                                <Label htmlFor={inputId("fecha")}>{t("candle.f.fecha")}</Label>
                                <input id={inputId("fecha")} data-testid="candle-fecha-input" type="date" className="form-input"
                                    value={form.fecha} onChange={set("fecha")} />
                            </div>
                            <div>
                                <Label htmlFor={inputId("signo")}>{t("candle.f.signo")}</Label>
                                <select id={inputId("signo")} data-testid="candle-signo-select" className="form-input"
                                    value={form.signo} onChange={set("signo")}>
                                    <option value="">{t("candle.selectSign")}</option>
                                    {signos.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label htmlFor={inputId("telefono")}>{t("candle.f.telefono")}</Label>
                                <input id={inputId("telefono")} data-testid="candle-telefono-input" type="tel" className="form-input"
                                    value={form.telefono} onChange={set("telefono")} placeholder="(602) 000-0000" autoComplete="tel" />
                            </div>
                            <div className="md:col-span-2">
                                <Label htmlFor={inputId("email")}>{t("candle.f.email")}</Label>
                                <input id={inputId("email")} data-testid="candle-email-input" type="email" className="form-input"
                                    value={form.email} onChange={set("email")} placeholder={t("candle.ph.email")} autoComplete="email" />
                            </div>
                            <div className="md:col-span-2">
                                <Label>{t("candle.f.paraQuien")}</Label>
                                <PillGroup name="para-quien" options={paraQuienOpts}
                                    value={form.paraQuien} onChange={set("paraQuien")} />
                            </div>
                            <div className="md:col-span-2">
                                <Label>{t("candle.f.intencion")}</Label>
                                <PillGroup name="intencion" options={intenciones.map((x) => ({ value: x, label: x }))}
                                    value={form.intencion} onChange={set("intencion")} />
                            </div>
                            <div className="md:col-span-2">
                                <Label htmlFor={inputId("situacion")}>{t("candle.f.situacion")}</Label>
                                <textarea id={inputId("situacion")} data-testid="candle-situacion-textarea" rows={4}
                                    className="form-input resize-y" value={form.situacion} onChange={set("situacion")}
                                    placeholder={t("candle.ph.situacion")} />
                            </div>
                            <div className="md:col-span-2">
                                <Label>{t("candle.f.entrega")}</Label>
                                <PillGroup name="entrega" options={entregaOpts}
                                    value={form.entrega} onChange={set("entrega")} />
                            </div>

                            {form.entrega === "envio" && (
                                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 border border-gold/20 rounded-sm p-5 sm:p-6 bg-ink/50">
                                    <div className="sm:col-span-2">
                                        <Label htmlFor={inputId("direccion")}>{t("candle.f.direccion")}</Label>
                                        <input id={inputId("direccion")} data-testid="candle-direccion-input" className="form-input"
                                            value={form.direccion} onChange={set("direccion")} placeholder={t("candle.ph.direccion")} autoComplete="street-address" />
                                    </div>
                                    <div>
                                        <Label htmlFor={inputId("ciudad")}>{t("candle.f.ciudad")}</Label>
                                        <input id={inputId("ciudad")} data-testid="candle-ciudad-input" className="form-input"
                                            value={form.ciudad} onChange={set("ciudad")} autoComplete="address-level2" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor={inputId("estado")}>{t("candle.f.estado")}</Label>
                                            <input id={inputId("estado")} data-testid="candle-estado-input" className="form-input"
                                                value={form.estado} onChange={set("estado")} autoComplete="address-level1" />
                                        </div>
                                        <div>
                                            <Label htmlFor={inputId("cp")}>{t("candle.f.cp")}</Label>
                                            <input id={inputId("cp")} data-testid="candle-cp-input" className="form-input"
                                                value={form.cp} onChange={set("cp")} autoComplete="postal-code" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="md:col-span-2">
                                <label className="flex items-start gap-3 cursor-pointer group" data-testid="candle-confirm-label">
                                    <input type="checkbox" data-testid="candle-confirm-checkbox"
                                        checked={form.confirm} onChange={set("confirm")}
                                        className="mt-0.5 w-4 h-4 shrink-0 appearance-none border border-gold/45 rounded-sm checked:bg-gold checked:border-gold transition-colors duration-200 cursor-pointer" />
                                    <span className="text-xs sm:text-sm text-cream/65 font-light group-hover:text-cream/85 transition-colors duration-300">
                                        {t("candle.f.confirm")}
                                    </span>
                                </label>
                            </div>

                            <div className="md:col-span-2 pt-2">
                                <button type="submit" data-testid="candle-submit-button"
                                    className="w-full bg-gold text-ink font-accent text-xs sm:text-sm tracking-[0.22em] uppercase font-bold px-8 py-4 rounded-sm hover:brightness-110 hover:shadow-[0_0_32px_rgba(212,175,55,0.4)] hover:-translate-y-0.5 transition-[box-shadow,transform,filter] duration-300">
                                    {t("candle.submit")}
                                </button>
                                <p className="text-[11px] text-sand text-center mt-4 font-light leading-relaxed">
                                    {t("candle.note")}
                                </p>
                            </div>
                        </div>
                    </form>
                </Reveal>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-32 section-fade-bottom" aria-hidden="true" />
        </section>
    );
};
